/**
 * The drawer's two path checkout (Cart Drawer.dc.html lines 74/75): a PROTECTED CHECKOUT
 * button that includes package protection, and a plain link below to skip it. This only
 * orchestrates sequencing, "make the add on line match what was just clicked, then submit"
 * never the add/remove network call itself (that stays inside assets/cart-protection.js's
 * own toggleAddOn(), triggered here through the exact same checkbox change event its own
 * markup already binds to) and never the actual checkout submission (that stays the theme's
 * own native #checkout submit button, kept in the drawer's markup but hidden, so payment
 * mechanics are untouched).
 */

const INIT_FLAG = 'cartProtectedCheckoutInit';

/**
 * Upper bound on waiting for the add on request. Slightly above cart-protection.js's own
 * REQUEST_TIMEOUT_MS, which is what normally ends a stalled request; this is only the backstop
 * that guarantees the buttons can never stay disabled for good.
 */
const TOGGLE_WAIT_LIMIT_MS = 12000;

if (!document.documentElement.dataset[INIT_FLAG]) {
  document.documentElement.dataset[INIT_FLAG] = 'true';

  /** @returns {HTMLInputElement | null} */
  const findToggle = () => {
    const toggle = document.querySelector('cart-protection [ref="toggle"]');
    return toggle instanceof HTMLInputElement ? toggle : null;
  };

  /**
   * Resolves with the live toggle once it is no longer mid request (assets/cart-protection.js
   * sets `disabled = true` synchronously before its own network call, and back to `false` once
   * that call, and the section morph after it, both finish), or once TOGGLE_WAIT_LIMIT_MS passes.
   * Re-queried on every poll because the drawer can be re-rendered while the request runs.
   * @returns {Promise<HTMLInputElement | null>}
   */
  const waitForToggle = () =>
    new Promise((resolve) => {
      const deadline = Date.now() + TOGGLE_WAIT_LIMIT_MS;
      // setTimeout, not requestAnimationFrame: rAF callbacks are fully suspended on a
      // backgrounded/hidden tab, which would hang this forever if the customer switches
      // apps mid click. setTimeout keeps firing (throttled, but firing) in the background.
      const poll = () => {
        const toggle = findToggle();
        if (!toggle || !toggle.disabled || Date.now() > deadline) {
          resolve(toggle);
          return;
        }
        setTimeout(poll, 50);
      };
      poll();
    });

  /** @param {boolean} visible */
  const setErrorVisible = (visible) => {
    const error = document.querySelector('[data-cart-checkout-error]');
    if (error instanceof HTMLElement) error.hidden = !visible;
  };

  document.addEventListener('click', async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-cart-checkout-protect]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    event.preventDefault();
    setErrorVisible(false);

    const wantsProtection = button.dataset.cartCheckoutProtect === 'true';
    const toggle = findToggle();
    const initialCheckout = document.getElementById('checkout');
    if (!(initialCheckout instanceof HTMLButtonElement) || initialCheckout.disabled) return;

    if (toggle && toggle.checked !== wantsProtection && !toggle.disabled) {
      const allProtectButtons = [...document.querySelectorAll('[data-cart-checkout-protect]')].filter(
        (otherButton) => otherButton instanceof HTMLButtonElement
      );
      allProtectButtons.forEach((otherButton) => {
        /** @type {HTMLButtonElement} */ (otherButton).disabled = true;
      });
      button.setAttribute('aria-busy', 'true');

      /** @type {HTMLInputElement | null} */
      let settled = null;
      try {
        toggle.checked = wantsProtection;
        toggle.dispatchEvent(new Event('change', { bubbles: true }));
        settled = await waitForToggle();
      } finally {
        // Always re-enabled: a request that never settles must not leave both checkout
        // buttons dead until the page is reloaded.
        allProtectButtons.forEach((otherButton) => {
          /** @type {HTMLButtonElement} */ (otherButton).disabled = false;
        });
        button.removeAttribute('aria-busy');
      }

      // toggleAddOn reverts the checkbox on a failed request. Don't send the customer to
      // checkout with an add on state that doesn't match what they just clicked, and say so:
      // cart-protection.js's own error lives inside its visually hidden element in this layout.
      if (!settled || settled.disabled || settled.checked !== wantsProtection) {
        setErrorVisible(true);
        return;
      }
    }

    // Read by assets/cart-protection.js, not here: this only flags "protection is riding along
    // to checkout right now" so that file can clean it back up if the customer abandons checkout
    // and lands back on the site with the add on still sitting in their cart (Shopify only
    // clears a cart automatically on a completed order, never on an abandoned one).
    if (wantsProtection) {
      try {
        sessionStorage.setItem('cartProtectionPendingCheckout', String(Date.now()));
      } catch {
        // Storage can be unavailable (private browsing on older Safari); checkout still goes ahead.
      }
    }

    // Looked up again: the drawer may have been re-rendered while the add on request ran, and a
    // detached button has no form to submit.
    const nativeCheckout = document.getElementById('checkout');
    if (nativeCheckout instanceof HTMLButtonElement && !nativeCheckout.disabled) nativeCheckout.click();
  });
}
